import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { fetchAuthSession } from '../utils/session';
import axios from 'axios';
import { setBusinessDataLoaded } from '../api/businessAccessClient';
import { setSentryBusinessContext } from '../sentry';
import { canSignInWithoutBusiness } from '../utils/sessionAccess';

interface BusinessData {
  id: string;
  name: string;
  api_key: string;
  logo_url?: string;
  website_url?: string;
  // Add other business fields as needed
}

interface BusinessContextType {
  business: BusinessData | null;
  availableBusinesses: BusinessData[];
  // SAM-535: the whole tenant's business list (unfiltered by the caller's own Cognito
  // business IDs), sorted by name. Only for screens like MerchantConfig where an
  // xhinola_admin needs to point at ANY business in the tenant — the backend enforces
  // tenant membership independently (merchant_admin._assert_business_in_tenant), so the
  // client just needs to offer the full list. Everything else should keep using
  // `availableBusinesses`.
  tenantBusinesses: BusinessData[];
  loading: boolean;
  error: string | null;
  refreshBusiness: () => Promise<void>;
  switchBusiness: (businessId: string) => Promise<void>;
  currentBusinessId: string | null;
  isSidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
}

// Exported for unit testing without needing to render the provider.
export const sortBusinessesByName = (businesses: BusinessData[]): BusinessData[] =>
  [...businesses].sort((a, b) => a.name.localeCompare(b.name));

const BusinessContext = createContext<BusinessContextType | undefined>(undefined);

export const useBusinessContext = () => {
  const context = useContext(BusinessContext);
  if (context === undefined) {
    throw new Error('useBusinessContext must be used within a BusinessProvider');
  }
  return context;
};

interface BusinessProviderProps {
  children: ReactNode;
}

export const BusinessProvider: React.FC<BusinessProviderProps> = ({ children }) => {
  const [business, setBusiness] = useState<BusinessData | null>(null);
  const [availableBusinesses, setAvailableBusinesses] = useState<BusinessData[]>([]);
  const [tenantBusinesses, setTenantBusinesses] = useState<BusinessData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState<boolean>(false);
  const [currentBusinessId, setCurrentBusinessId] = useState<string | null>(null);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);

  // SAM-226: tag Sentry events with the business the operator is working in, so
  // a reported error is traceable to a tenant. No-op when Sentry is disabled.
  useEffect(() => {
    setSentryBusinessContext({
      businessId: currentBusinessId ?? business?.id ?? null,
      businessName: business?.name ?? null,
    });
  }, [currentBusinessId, business?.id, business?.name]);

  const fetchBusinessesData = async (businessIds: string[], token: string) => {
    try {
      setLoading(true);
      setError(null);
      
      
      // Make request to general business endpoint without ID parameter
      const response = await axios({
        url: `${import.meta.env.VITE_API_BASE_URL}/crud/business`,
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data && response.data.data && response.data.data.length > 0) {
        const allBusinesses = response.data.data as BusinessData[];

        // SAM-535: keep the unfiltered tenant list too, sorted by name, for screens
        // (e.g. MerchantConfig) that need to see every business in the tenant rather
        // than just the ones tied to the caller's own Cognito business_id claim.
        setTenantBusinesses(sortBusinessesByName(allBusinesses));

        // Filter businesses that match the user's business IDs
        const businessesData = allBusinesses.filter((business: BusinessData) =>
          businessIds.includes(business.id)
        );

        if (businessesData.length === 0) {
          console.error("No matching businesses found for user IDs");
          setError('No matching businesses found');
          setLoading(false);
          return;
        }
        
        setAvailableBusinesses(businessesData);
        
        // Get selected business from storage or use first one
        let selectedBusinessId = sessionStorage.getItem('selectedBusinessId');
        
        // If stored ID is not in available businesses, use first one
        if (!selectedBusinessId || !businessesData.some((b: BusinessData) => b.id === selectedBusinessId)) {
          selectedBusinessId = businessesData[0].id;
          sessionStorage.setItem('selectedBusinessId', selectedBusinessId);
        }
        
        setCurrentBusinessId(selectedBusinessId);
        
        // Set the active business
        const selectedBusiness = businessesData.find((b: BusinessData) => b.id === selectedBusinessId) || businessesData[0];
        setBusiness(selectedBusiness);
        sessionStorage.setItem('businessData', JSON.stringify(selectedBusiness));
        
        // Signal that business data is now available for API requests
        setBusinessDataLoaded();
      } else {
        console.error("No business data found in response:", response.data);
        setError('No business data found');
      }
    } catch (error) {
      console.error('🚨 Error fetching business data:', error);
      console.error('Business Context Error Details:', {
        status: (error as any)?.status,
        message: (error as any)?.message,
        response: (error as any)?.response,
      });
      setError('Failed to fetch business data');
    } finally {
      // SAM-148/SAM-240: never leave a request queue blocked — queued calls
      // proceed (with the fallback API key if needed) so auth failures surface
      // as 401s and trigger the token-expiration redirect instead of an
      // infinite spinner. setBusinessDataLoaded() flushes BOTH the httpClient
      // queue and the businessAccessClient queue (SAM-148 only flushed the
      // former, which left dashboard/business-access calls hanging forever
      // whenever this fetch failed).
      setBusinessDataLoaded();
      setLoading(false);
      setInitialized(true);
    }
  };

  const fetchBusinessData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get the current auth session to access the token
      const session = await fetchAuthSession();
      if (!session.tokens?.idToken) {
        setError('No authentication token available');
        setBusinessDataLoaded();
        setLoading(false);
        return;
      }

      const token = session.tokens.idToken.toString();
      
      // Parse token to get business_id
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) {
        setError('Invalid token format');
        setBusinessDataLoaded();
        setLoading(false);
        return;
      }

      // Decode token payload
      const claims = JSON.parse(atob(tokenParts[1]));
      const businessIdValue = claims['custom:business_id'];
      const userGroups: string[] = claims['cognito:groups'] || [];
      if (!businessIdValue) {
        if (canSignInWithoutBusiness(userGroups)) {
          // Property managers (via user_estate) and the tenant Front Desk are not tied to a business.
          // Unblock the request queue so API calls can proceed using the fallback API key.
          setBusinessDataLoaded();
          setLoading(false);
          setInitialized(true);
          return;
        }
        setError('User is not associated with any business');
        setBusinessDataLoaded();
        setLoading(false);
        return;
      }

      // Check if user has multiple businesses (comma-separated)
      const businessIds = businessIdValue.split(',').map((id: string) => id.trim());
      
      // Fetch all businesses data
      await fetchBusinessesData(businessIds, token);
    } catch (error) {
      console.error('Error fetching business data:', error);
      setError('Failed to fetch business data');
      setBusinessDataLoaded();
      setLoading(false);
      setInitialized(true);
    }
  };

  // Switch to a different business
  const switchBusiness = async (businessId: string) => {
    try {
      setLoading(true);
      
      // Find business in available businesses
      const selectedBusiness = availableBusinesses.find(b => b.id === businessId);
      
      if (!selectedBusiness) {
        console.error("Business not found in available businesses:", businessId, availableBusinesses);
        setError('Business not found');
        setLoading(false);
        return;
      }
      
      
      // Set as current business
      setBusiness(selectedBusiness);
      setCurrentBusinessId(businessId);
      sessionStorage.setItem('businessData', JSON.stringify(selectedBusiness));
      sessionStorage.setItem('selectedBusinessId', businessId);
      
      
      // Signal that business data is now available for API requests
      setBusinessDataLoaded();

    } catch (error) {
      console.error('Error switching business:', error);
      setError('Failed to switch business');
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    
    // Check if we have cached data
    const cachedData = sessionStorage.getItem('businessData');
    if (cachedData) {
      try {
        const businessData = JSON.parse(cachedData) as BusinessData;
        setBusiness(businessData);
        setCurrentBusinessId(businessData.id);
        
        // Still fetch all available businesses for switching
        const token = localStorage.getItem("token");
        if (token) {
          fetchBusinessData();
        } else {
          // No token available, mark as initialized but not loaded
          setLoading(false);
          setInitialized(true);
        }
        
        // Signal that business data is now available for API requests
        setBusinessDataLoaded();
      } catch (e) {
        console.error("Error parsing cached business data:", e);
        // If cached data is invalid, fetch fresh data
        fetchBusinessData();
      }
    } else {
      const token = localStorage.getItem("token");
      if (token) {
        fetchBusinessData();
      } else {
        // No token available, mark as initialized but not loaded
        setLoading(false);
        setInitialized(true);
      }
    }
  }, []);

  const refreshBusiness = async () => {
    // Clear cached data
    sessionStorage.removeItem('businessData');
    // Fetch fresh data
    await fetchBusinessData();
  };

  const setSidebarCollapsed = (collapsed: boolean) => {
    setIsSidebarCollapsed(collapsed);
  };

  return (
    <BusinessContext.Provider 
      value={{
        business,
        availableBusinesses,
        tenantBusinesses,
        loading,
        error, 
        refreshBusiness,
        switchBusiness,
        currentBusinessId,
        isSidebarCollapsed,
        setSidebarCollapsed
      }}
    >
      {children}
    </BusinessContext.Provider>
  );
}; 